import { createContext, useContext, useState, ReactNode, useRef, ReactElement } from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button, { ButtonProps } from '@mui/material/Button';
import { Typography } from '@mui/material';

type DialogOptions = {
  title?: string;
  content?: string;
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  dialogProps?: Partial<DialogProps>;
  cancelText?: ReactElement | string;
  okText?: ReactElement | string;
  onConfirm?: () => void;
  onClose?: () => void;
};

type DialogContextType = {
  openDialog: (options: DialogOptions) => Promise<boolean>;
  closeDialog: () => void;
};

export const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [dialogOptions, setDialogOptions] = useState<DialogOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const queue = useRef<Promise<boolean>>(Promise.resolve(false));
  const resolver = useRef<(value: boolean | PromiseLike<boolean>) => void>(null);

  const openDialog = (options: DialogOptions) => {
    queue.current = new Promise((resolve) => {
      resolver.current = resolve;
      setDialogOptions(options);
    });
    return queue.current;
  };

  const closeDialog = () => {
    setDialogOptions(null);
    resolver.current?.(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    if (dialogOptions?.onConfirm) {
      try {
        await dialogOptions.onConfirm();
      } catch (error) {
        console.error('Error executing onConfirm:', error);
      }
    }
    resolver.current?.(true);
    setDialogOptions(null);
    setLoading(false);
  };

  return (
    <DialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      {dialogOptions && (
        <Dialog open={!!dialogOptions} onClose={closeDialog} {...dialogOptions.dialogProps}>
          <DialogTitle
            typography={'body1'}
            color={'text.primary'}
            sx={{ textAlign: 'center', fontSize: '16px', padding: '24px 16px 16px' }}
          >
            {dialogOptions.title}
          </DialogTitle>
          <DialogContent
            sx={{
              padding: '8px 16px',
            }}
          >
            <DialogContentText></DialogContentText>
            <Typography
              variant="body3"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                display: 'block',
              }}
            >
              {dialogOptions.content}
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              padding: '8px 16px 24px',
            }}
          >
            <Button
              onClick={closeDialog}
              color="standard1"
              variant="contained"
              sx={{ flex: 1, borderRadius: '2px', fontWeight: '500' }}
              {...(dialogOptions?.cancelButtonProps ?? {})}
            >
              {dialogOptions.cancelText ?? 'Cancel'}
            </Button>
            <Button
              onClick={handleConfirm}
              color="primary"
              variant="contained"
              sx={{ flex: 1, borderRadius: '2px', fontWeight: '500' }}
              loading={loading}
              {...(dialogOptions?.okButtonProps ?? {})}
            >
              {dialogOptions.okText ?? 'Ok'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </DialogContext.Provider>
  );
};

export default DialogProvider;
